//@ts-nocheck
import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import Card, {
  CardBody,
  CardHeader,
  CardLabel,
  CardTitle,
} from '../../../components/bootstrap/Card';
import Icon from '../../../components/icon/Icon';
import LoaderWrapper from '../../../layout/Wrapper/LoaderWrapper';
import Button from '../../../components/bootstrap/Button';
import axios from 'axios';
import { BASE_URL } from '../../../shared/constants/globalContants';
import Modal, {
  ModalBody,
  ModalHeader,
  ModalTitle,
} from '../../../components/bootstrap/Modal';
import Badge from '../../../components/bootstrap/Badge';
import moment from 'moment';
import auth from '../../../shared/mobx/auth-state';
import notificationState from '../../../shared/mobx/notification-state';
import styles from './QueueManagement.module.scss';

interface QueueStats {
  name: string;
  status: string;
  waiting: number;
  active: number;
  completed: number;
  failed: number;
  delayed: number;
  paused: number;
  total: number;
}

interface Job {
  id: string;
  status: string;
  data: any;
  timestamp: number;
  processedOn?: number;
  finishedOn?: number;
  failedReason?: string;
  attemptsMade?: number;
  delay?: number;
  progress?: number;
}

const QueueManagement = () => {
  const [queuesOverview, setQueuesOverview] = useState<QueueStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedQueue, setSelectedQueue] = useState<string | null>(null);
  const [queueDetails, setQueueDetails] = useState<any>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showJobModal, setShowJobModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const [failedJobs, setFailedJobs] = useState<any[]>([]);
  const [failedLoading, setFailedLoading] = useState(false);

  const fetchQueuesOverview = async () => {
    try {
      const token = auth.currentUser?.authToken;
      
      // Debug: Check token and user role
      console.log('🔍 Current User:', auth.currentUser);
      console.log('🔍 User role:', auth.currentUser?.role);
      console.log('🔍 Is Admin?:', auth.isAdmin);
      
      if (!token) {
        console.error('❌ No token found - user not logged in');
        notificationState.addToasterError('Please log in to access this page');
        return;
      }
      
      if (!auth.isAdmin) {
        console.error('❌ User is not an admin');
        notificationState.addToasterError('You must be logged in as an admin to access this page');
        return;
      }
      
      console.log('🔍 Making request to:', `${BASE_URL}admin/queues/overview`);
      console.log('🔍 Authorization header:', `Bearer ${token?.substring(0, 20)}...`);
      
      const response = await axios.get(`${BASE_URL}admin/queues/overview`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.data.success) {
        setQueuesOverview(response.data.data);
      }
    } catch (error: any) {
      console.error('❌ Error fetching queues overview:', error);
      console.error('❌ Error response:', error?.response?.data);
      
      if (error?.response?.status === 401) {
        notificationState.addToasterError('Authentication failed. Please make sure you are logged in as an admin user.');
      } else {
        notificationState.addToasterError('Failed to fetch queue overview');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchQueueDetails = async (queueName: string) => {
    setDetailsLoading(true);
    try {
      const token = auth.currentUser?.authToken;
      const response = await axios.get(
        `${BASE_URL}admin/queues/${queueName}/details`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        setQueueDetails(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching queue details:', error);
      notificationState.addToasterError('Failed to fetch queue details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const fetchFailedJobs = async (queueName: string) => {
    setFailedLoading(true);
    try {
      const token = auth.currentUser?.authToken;
      const response = await axios.get(
        `${BASE_URL}admin/queues/${queueName}/failed`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        setFailedJobs(response.data.data.failedJobs);
        setShowFailedModal(true);
      }
    } catch (error: any) {
      console.error('Error fetching failed jobs:', error);
      notificationState.addToasterError('Failed to fetch failed jobs');
    } finally {
      setFailedLoading(false);
    }
  };

  const handleRetryJob = async (queueName: string, jobId: string) => {
    try {
      const token = auth.currentUser?.authToken;
      const response = await axios.post(
        `${BASE_URL}admin/queues/${queueName}/jobs/${jobId}/retry`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        notificationState.addToasterSuccess('Job queued for retry');
        if (selectedQueue) {
          fetchQueueDetails(selectedQueue);
        }
        setShowFailedModal(false);
      }
    } catch (error: any) {
      console.error('Error retrying job:', error);
      notificationState.addToasterError('Failed to retry job');
    }
  };

  const handleRemoveJob = async (queueName: string, jobId: string) => {
    if (!window.confirm('Are you sure you want to remove this job?')) {
      return;
    }
    try {
      const token = auth.currentUser?.authToken;
      const response = await axios.post(
        `${BASE_URL}admin/queues/${queueName}/jobs/${jobId}/remove`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        notificationState.addToasterSuccess('Job removed successfully');
        if (selectedQueue) {
          fetchQueueDetails(selectedQueue);
        }
        setShowJobModal(false);
      }
    } catch (error: any) {
      console.error('Error removing job:', error);
      notificationState.addToasterError('Failed to remove job');
    }
  };

  const handleCleanQueue = async (queueName: string, status: string) => {
    if (!window.confirm(`Are you sure you want to clean all ${status} jobs?`)) {
      return;
    }
    try {
      const token = auth.currentUser?.authToken;
      const response = await axios.post(
        `${BASE_URL}admin/queues/${queueName}/clean`,
        { status, grace: 0 },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        notificationState.addToasterSuccess(response.data.message);
        fetchQueuesOverview();
        if (selectedQueue) {
          fetchQueueDetails(selectedQueue);
        }
      }
    } catch (error: any) {
      console.error('Error cleaning queue:', error);
      notificationState.addToasterError('Failed to clean queue');
    }
  };

  const handleClearActiveJobs = async (queueName: string) => {
    if (!window.confirm('Are you sure you want to clear all active jobs? This will stop currently running jobs.')) {
      return;
    }
    try {
      const token = auth.currentUser?.authToken;
      const response = await axios.post(
        `${BASE_URL}admin/queues/${queueName}/clear-active`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        notificationState.addToasterSuccess(response.data.message);
        fetchQueuesOverview();
        if (selectedQueue) {
          fetchQueueDetails(selectedQueue);
        }
      }
    } catch (error: any) {
      console.error('Error clearing active jobs:', error);
      notificationState.addToasterError('Failed to clear active jobs');
    }
  };

  const handlePauseQueue = async (queueName: string) => {
    try {
      const token = auth.currentUser?.authToken;
      const response = await axios.post(
        `${BASE_URL}admin/queues/${queueName}/pause`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        notificationState.addToasterSuccess('Queue paused successfully');
        fetchQueuesOverview();
      }
    } catch (error: any) {
      console.error('Error pausing queue:', error);
      notificationState.addToasterError('Failed to pause queue');
    }
  };

  const handleResumeQueue = async (queueName: string) => {
    try {
      const token = auth.currentUser?.authToken;
      const response = await axios.post(
        `${BASE_URL}admin/queues/${queueName}/resume`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) {
        notificationState.addToasterSuccess('Queue resumed successfully');
        fetchQueuesOverview();
      }
    } catch (error: any) {
      console.error('Error resuming queue:', error);
      notificationState.addToasterError('Failed to resume queue');
    }
  };

  const viewJobDetails = (job: Job) => {
    setSelectedJob(job);
    setShowJobModal(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'waiting':
        return 'info';
      case 'delayed':
        return 'warning';
      case 'failed':
        return 'danger';
      case 'completed':
        return 'primary';
      case 'unavailable':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getQueueDisplayName = (name: string) => {
    const names: Record<string, string> = {
      pdfGenerateQueue: 'PDF Generation',
      bulkPdfQueue: 'Bulk PDF',
      mergePdfQueue: 'Merge PDF',
      pdfDeleteQueue: 'PDF Deletion',
    };
    return names[name] || name;
  };

  useEffect(() => {
    fetchQueuesOverview();
  }, []);

  useEffect(() => {
    if (selectedQueue) {
      fetchQueueDetails(selectedQueue);
    }
  }, [selectedQueue]);

  return (
    <div className={styles.queueManagementContainer}>
      <div className="row mt-4">
        <div className="col-sm-12">
          <Card className="transition-base rounded-2 mb-4 text-dark" shadow="sm">
            <CardHeader className="bg-transparent" style={{ padding: '1.5rem' }}>
              <div className={styles.headerSection}>
                <CardTitle tag="div" className={styles.pageTitle}>
                  <Icon icon="Queue" size="2x" />
                  <span>Queue & Jobs Management</span>
                </CardTitle>
                <Button
                  color="primary"
                  icon="Refresh"
                  size="lg"
                  onClick={() => {
                    fetchQueuesOverview();
                    if (selectedQueue) {
                      fetchQueueDetails(selectedQueue);
                    }
                  }}
                >
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardBody style={{ padding: '1.5rem' }}>
              <LoaderWrapper isLoading={loading} data={queuesOverview}>
                <div className="row g-4">
                  {queuesOverview.map((queue) => (
                    <div className="col-sm-6 col-xl-3" key={queue.name}>
                      <Card
                        className={classNames(styles.queueCard, {
                          [styles.selected]: selectedQueue === queue.name,
                          'border-success': queue.status === 'active' && selectedQueue !== queue.name,
                          'border-danger': queue.status === 'unavailable',
                        })}
                        onClick={() => setSelectedQueue(queue.name)}
                        shadow="sm"
                      >
                        <div className={styles.queueCardBody}>
                          <div className={styles.queueHeader}>
                            <h6 className={styles.queueTitle}>
                              {getQueueDisplayName(queue.name)}
                            </h6>
                            <Badge 
                              color={getStatusColor(queue.status)}
                              className={styles.statusBadge}
                            >
                              {queue.status}
                            </Badge>
                          </div>
                          
                          <div className={styles.statsGrid}>
                            <div className={styles.statItem}>
                              <span className={classNames(styles.statValue, styles.primary)}>
                                {queue.active}
                              </span>
                              <span className={styles.statLabel}>Active</span>
                            </div>
                            <div className={styles.statItem}>
                              <span className={classNames(styles.statValue, styles.info)}>
                                {queue.waiting}
                              </span>
                              <span className={styles.statLabel}>Waiting</span>
                            </div>
                            <div className={styles.statItem}>
                              <span className={classNames(styles.statValue, styles.danger)}>
                                {queue.failed}
                              </span>
                              <span className={styles.statLabel}>Failed</span>
                            </div>
                          </div>
                          
                          <div className={styles.statsGridSecondary}>
                            <div className={styles.statItem}>
                              <span className={classNames(styles.statValue, styles.warning)}>
                                {queue.delayed}
                              </span>
                              <span className={styles.statLabel}>Delayed</span>
                            </div>
                            <div className={styles.statItem}>
                              <span className={classNames(styles.statValue, styles.success)}>
                                {queue.completed}
                              </span>
                              <span className={styles.statLabel}>Completed</span>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </div>
                  ))}
                </div>
              </LoaderWrapper>
            </CardBody>
          </Card>
        </div>
      </div>

      {selectedQueue && (
        <div className="row mb-4">
          <div className="col-sm-12">
            <Card className={classNames("transition-base rounded-2 text-dark", styles.detailsCard)} shadow="sm">
              <CardHeader className="bg-transparent" style={{ padding: '1.5rem' }}>
                <div className={styles.headerSection}>
                  <CardTitle tag="div" className="h5 mb-0">
                    <Icon icon="List" className="me-2" />
                    {getQueueDisplayName(selectedQueue)} - Job Details
                  </CardTitle>
                  <div className={styles.actionButtons}>
                    <Button
                      color="warning"
                      size="sm"
                      icon="Pause"
                      onClick={() => handlePauseQueue(selectedQueue)}
                    >
                      Pause
                    </Button>
                    <Button
                      color="success"
                      size="sm"
                      icon="PlayArrow"
                      onClick={() => handleResumeQueue(selectedQueue)}
                    >
                      Resume
                    </Button>
                    <Button
                      color="danger"
                      size="sm"
                      icon="StopCircle"
                      onClick={() => handleClearActiveJobs(selectedQueue)}
                    >
                      Clear Active
                    </Button>
                    <Button
                      color="warning"
                      size="sm"
                      icon="CleaningServices"
                      onClick={() => handleCleanQueue(selectedQueue, 'waiting')}
                    >
                      Clear Waiting
                    </Button>
                    <Button
                      color="danger"
                      size="sm"
                      icon="Delete"
                      onClick={() => handleCleanQueue(selectedQueue, 'failed')}
                    >
                      Clean Failed
                    </Button>
                    <Button
                      color="primary"
                      size="sm"
                      icon="CleaningServices"
                      onClick={() => handleCleanQueue(selectedQueue, 'completed')}
                    >
                      Clean Completed
                    </Button>
                    <Button
                      color="info"
                      size="sm"
                      icon="ErrorOutline"
                      onClick={() => fetchFailedJobs(selectedQueue)}
                    >
                      View Failed
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardBody style={{ padding: '1.5rem' }}>
                <LoaderWrapper isLoading={detailsLoading} data={queueDetails}>
                  {queueDetails && (
                    <>
                      <div className={styles.alertBox}>
                        <strong>Queue Statistics:</strong> 
                        <span className="ms-2">
                          Total: <strong>{queueDetails.counts.total}</strong> | 
                          Active: <strong>{queueDetails.counts.active}</strong> | 
                          Waiting: <strong>{queueDetails.counts.waiting}</strong> | 
                          Delayed: <strong>{queueDetails.counts.delayed}</strong> | 
                          Completed: <strong>{queueDetails.counts.completed}</strong> | 
                          Failed: <strong className="text-danger">{queueDetails.counts.failed}</strong>
                        </span>
                      </div>
                      
                      <div className={styles.tableContainer}>
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th>Job ID</th>
                              <th>Status</th>
                              <th>Created</th>
                              <th>Delay</th>
                              <th className="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {queueDetails.jobs.map((job: Job) => (
                              <tr key={job.id}>
                                <td>
                                  <code>{job.id}</code>
                                </td>
                                <td>
                                  <Badge color={getStatusColor(job.status)}>
                                    {job.status}
                                  </Badge>
                                </td>
                                <td>
                                  {moment(job.timestamp).format('MMM DD, YYYY HH:mm:ss')}
                                </td>
                                <td>
                                  {job.delay && job.delay > 0
                                    ? `${Math.round(job.delay / 1000 / 60)}m`
                                    : '-'}
                                </td>
                                <td className="text-end">
                                  <div className="d-flex gap-2 justify-content-end">
                                    <Button
                                      color="info"
                                      size="sm"
                                      icon="Visibility"
                                      onClick={() => viewJobDetails(job)}
                                    >
                                      View
                                    </Button>
                                    {job.status === 'failed' && (
                                      <Button
                                        color="warning"
                                        size="sm"
                                        icon="Refresh"
                                        onClick={() =>
                                          handleRetryJob(selectedQueue, job.id)
                                        }
                                      >
                                        Retry
                                      </Button>
                                    )}
                                    <Button
                                      color="danger"
                                      size="sm"
                                      icon="Close"
                                      onClick={() =>
                                        handleRemoveJob(selectedQueue, job.id)
                                      }
                                    >
                                      Remove
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {queueDetails.jobs.length === 0 && (
                          <div className={styles.emptyState}>
                            <Icon icon="Inbox" size="3x" />
                            <div className="h6">No jobs in this queue</div>
                            <p className="mb-0">There are currently no active, waiting, or delayed jobs.</p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </LoaderWrapper>
              </CardBody>
            </Card>
          </div>
        </div>
      )}

      {/* Job Details Modal */}
      <Modal
        isOpen={showJobModal}
        setIsOpen={setShowJobModal}
        size="lg"
        titleId="jobDetailsModal"
      >
        <ModalHeader setIsOpen={setShowJobModal}>
          <ModalTitle id="jobDetailsModal">
            <Icon icon="Assignment" className="me-2" />
            Job Details
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          {selectedJob && (
            <div className={styles.modalContent}>
              <div className="mb-3">
                <strong>Job ID:</strong> <code>{selectedJob.id}</code>
              </div>
              
              <div className="mb-3">
                <strong>Status:</strong>{' '}
                <Badge color={getStatusColor(selectedJob.status)} className="ms-2">
                  {selectedJob.status}
                </Badge>
              </div>
              
              <div className="mb-3">
                <strong>Created:</strong>{' '}
                <span className="text-muted">
                  {moment(selectedJob.timestamp).format('MMM DD, YYYY HH:mm:ss')}
                </span>
              </div>
              
              {selectedJob.processedOn && (
                <div className="mb-3">
                  <strong>Processed:</strong>{' '}
                  <span className="text-muted">
                    {moment(selectedJob.processedOn).format('MMM DD, YYYY HH:mm:ss')}
                  </span>
                </div>
              )}
              
              {selectedJob.finishedOn && (
                <div className="mb-3">
                  <strong>Finished:</strong>{' '}
                  <span className="text-muted">
                    {moment(selectedJob.finishedOn).format('MMM DD, YYYY HH:mm:ss')}
                  </span>
                </div>
              )}
              
              {selectedJob.failedReason && (
                <div className="alert alert-danger mt-3">
                  <strong>
                    <Icon icon="ErrorOutline" className="me-2" />
                    Failed Reason:
                  </strong>
                  <div className="mt-2">{selectedJob.failedReason}</div>
                </div>
              )}
              
              <div className="mb-3">
                <strong>Attempts Made:</strong>{' '}
                <Badge color="secondary" className="ms-2">
                  {selectedJob.attemptsMade || 0}
                </Badge>
              </div>
              
              <h6 className="mt-4 mb-3">
                <Icon icon="DataObject" className="me-2" />
                Job Data:
              </h6>
              <pre className={styles.codeBlock}>
                {JSON.stringify(selectedJob.data, null, 2)}
              </pre>
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* Failed Jobs Modal */}
      <Modal
        isOpen={showFailedModal}
        setIsOpen={setShowFailedModal}
        size="xl"
        titleId="failedJobsModal"
      >
        <ModalHeader setIsOpen={setShowFailedModal}>
          <ModalTitle id="failedJobsModal">
            <Icon icon="ErrorOutline" className="me-2" />
            Failed Jobs - {selectedQueue && getQueueDisplayName(selectedQueue)}
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          <LoaderWrapper isLoading={failedLoading} data={failedJobs}>
            <div className={styles.tableContainer}>
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Job ID</th>
                    <th>Failed At</th>
                    <th>Reason</th>
                    <th>Attempts</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {failedJobs.map((job) => (
                    <tr key={job.id}>
                      <td>
                        <code>{job.id}</code>
                      </td>
                      <td>
                        {job.finishedOn
                          ? moment(job.finishedOn).format('MMM DD, YYYY HH:mm:ss')
                          : <span className="text-muted">N/A</span>}
                      </td>
                      <td>
                        <div className="text-danger" style={{ maxWidth: '400px' }}>
                          <small>{job.failedReason || 'Unknown error'}</small>
                        </div>
                      </td>
                      <td>
                        <Badge color="secondary">{job.attemptsMade || 0}</Badge>
                      </td>
                      <td className="text-end">
                        <div className="d-flex gap-2 justify-content-end">
                          <Button
                            color="warning"
                            size="sm"
                            icon="Refresh"
                            onClick={() =>
                              handleRetryJob(selectedQueue!, job.id)
                            }
                          >
                            Retry
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            icon="Delete"
                            onClick={() =>
                              handleRemoveJob(selectedQueue!, job.id)
                            }
                          >
                            Remove
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {failedJobs.length === 0 && (
                <div className={styles.emptyState}>
                  <Icon icon="CheckCircle" size="3x" color="success" />
                  <div className="h6 mt-3">No Failed Jobs</div>
                  <p className="mb-0 text-muted">All jobs completed successfully!</p>
                </div>
              )}
            </div>
          </LoaderWrapper>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default QueueManagement;
